import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import { Form, Select, Input, Checkbox } from '@block/plug';
import { SuccessFile } from '@src/types/self';
import { Upload } from '../../../../components';
import { CertificateCodeItemKeyType, CertificateCodeItemLabelType, CertificateType } from '../../types';
import { showUnableUploadReasonIndex } from '../../consts';
import { ImagePreviewModal } from '../ImagePreviewModal';
import { ConfirmModal } from './ConfirmModal';
import { ReasonTips } from './ReasonTips';
import { useFormDataHandler, useOcrRecognition, useImagePreview } from './hooks';
import { fetchReasonList } from './utils';
import { CERTIFICATE_LABELS, getReasonLabel, CERTIFICATE_TYPES_WITH_CODE, OCR_CERTIFICATE_TYPES } from './constants';
import { UploadCertificateProps, UploadCertificateRef } from './types';
import Url from '../../url';
import './style.scss';

/**
 * 上传证书组件
 */
export const UploadCertificate = forwardRef<UploadCertificateRef, UploadCertificateProps>(
  (
    {
      // 证书类型
      certificateType,
      // 当前教育经历数据
      data,
      // 是否必填
      required = false,
      // 当前坑位附件等数据
      value,
      // 模式
      mode = 'edit',
      // 变更
      onChange = () => {},
      // 是否禁用
      disabled,
      // label宽度，预览的时候需要宽一点，对齐
      labelWidth = '120px',
      // 无法上传原因列表
      unableReasonList = [],
      // 是否全日制
      isFullTime = true,
    },
    ref,
  ) => {
    const formRef = useRef<Form>(null);

    // 初次渲染时，获取所有原因列表
    // 存储原因列表的完整数据
    const [reasonList, setReasonList] = useState<any[]>(unableReasonList);

    // 证书编码 动态的 formItemKey，没有就是undefined
    const codeItemKey = CertificateCodeItemKeyType?.[certificateType];

    // 证书编码 label
    const codeItemLabel = CertificateCodeItemLabelType?.[certificateType];

    // 使用自定义钩子管理表单数据
    const {
      formState,
      // 文件类型 实时更新
      fileType,
      setFormData,
      resetFormData,
      initialFormData,
      handleFieldValueChange,
    } = useFormDataHandler(
      value,
      onChange,
      formRef,
      codeItemKey,
      unableReasonList,
      certificateType,
      //
      isFullTime,
    );

    // 使用OCR识别钩子
    const { confirmModalState, fetchOcrCode, handleConfirmOk, handleConfirmCancel } = useOcrRecognition(
      formState,
      formRef,
      codeItemKey,
      onChange,
    );

    // 使用图片预览钩子
    const { imagePreviewState, openImagePreview, closeImagePreview } = useImagePreview();

    // 处理文件变更（上传成功或删除）
    const handleFileChange = async (file: SuccessFile) => {
      // 正常上传操作
      const { fileId, fileName, fileUrl } = file;

      // 检查是否为删除操作（file为空对象）
      if (!fileId) {
        // 删除操作，只清空文件相关字段，不清空证书编码和 attachmentFileType
        const updatedData = {
          fileId: undefined,
          fileName: undefined,
          fileUrl: undefined,
        };

        // 更新文件信息
        handleFieldValueChange('fileUrl', updatedData);
        return;
      }

      // 有fileId，则是上传操作，清空校验
      formRef.current?.clearValidateStatus(['fileUrl']);

      // 附件内容不需要OCR弹窗，直接更新表单数据
      await handleFieldValueChange('fileUrl', {
        fileId,
        fileName,
        fileUrl,
      });
      // 编码不同才会引起OCR弹窗提示

      // 检查是否需要进行OCR识别
      if (
        // 条件1：证书类型为毕业证书或学位证书
        OCR_CERTIFICATE_TYPES.includes(certificateType) &&
        // 条件2：可以上传（非不能上传状态）
        !formState.unableUpload
      ) {
        // 调用OCR识别接口获取证书编码
        await fetchOcrCode(file);
      }
    };

    // 获取原因列表
    const handleFetchReason = async () => {
      const list = await fetchReasonList(certificateType);
      setReasonList(list);
      return list;
    };

    useImperativeHandle(ref, () => ({
      getFormData: () => {
        // 合并表单数据和内部状态，确保返回符合CertificateData接口的数据
        const formData = formRef.current?.getFieldsValueIncludeHide() || {};

        // 从 reason 对象中提取 reasonType 和 reasonIndex
        const reason = formData.reason || formState.reason;
        const reasonType = reason?.reasonType;
        const unableUploadReason = reason?.reasonIndex;

        return {
          ...formState,
          ...formData,
          reason,
          unableUpload: formData.unableUpload ?? formState.unableUpload,
          // 添加从 reason 对象中提取的字段
          reasonType,
          unableUploadReason,
          // 保留 attachmentFileType，确保它是最新的被传递回去
          attachmentFileType: fileType,
        };
      },
      setFormData,
      validate: async () => {
        if (!required) return true;
        try {
          const res = await formRef.current?.validateFields();
          return res;
        } catch (error) {
          return false;
        }
      },
      reset: resetFormData,
    }));

    // 获取reasonIndex的数字表示
    const getReasonIndexNumber = (): number | undefined => {
      const reasonIndex = formState.reason?.reasonIndex;
      return typeof reasonIndex === 'number' ? reasonIndex : undefined;
    };

    // 判断是否为编辑模式
    const isEditMode = mode !== 'view';

    // 判断是否展示【无法上传】勾选框，编辑模式下、必填、非在读证明
    const shouldShowUnableUploadCheckbox = isEditMode && required && certificateType !== CertificateType.StudyProof;

    // 判断是否无法上传
    const isUnableToUpload = !!formState?.unableUpload;

    // 判断是否需要显示上传原因选择
    const shouldShowReasonSelect = isUnableToUpload;

    // 判断是否需要显示文件上传
    const shouldShowFileUpload = !isUnableToUpload;

    // 判断是否需要显示证书编码输入 - 仅对毕业证书和学位证书显示
    const shouldShowCertificateCode = CERTIFICATE_TYPES_WITH_CODE.includes(certificateType);

    // 判断是否需要显示无法上传原因的文件上传 - 仅对特定原因索引显示
    const reasonIndex = getReasonIndexNumber();

    // 判断是否需要显示无法上传原因的文件上传 - 仅对特定原因索引显示
    const shouldShowReasonFileUpload = reasonIndex !== undefined && showUnableUploadReasonIndex.includes(reasonIndex);

    // 获取上传组件的标签
    const getUploadLabel = () => {
      if (shouldShowReasonFileUpload) {
        return getReasonLabel(certificateType, reasonIndex, data);
      }
      return CERTIFICATE_LABELS[certificateType];
    };

    const renderViewFile = () => {
      if (formState?.fileUrl) {
        return <img className="preview-img" src={formState?.fileUrl} alt="" />;
      }
      return <span>无</span>;
    };

    // 初次渲染的时候，需要初始化表单数据
    useEffect(() => {
      // 后续切换的时候，是重新加载了组件，所以会重新执行useEffect，这个时候会带上第一次从后端获取的数据
      initialFormData(value);
      // attachmentFileType 会实时变更，需要保证attachmentFileType不为空，不会被map找不到，这样就不会重新渲染成出事数据
    }, []);

    return (
      <div className="certificateForm">
        <Form
          mode={mode}
          labelWidth={labelWidth}
          className="education__card"
          ref={formRef}
          onFieldValueChange={handleFieldValueChange}
        >
          <Form.Item span={0} label={`${CERTIFICATE_LABELS[certificateType]}：`} required={required} />

          {/* 无法上传选择框 - 仅在编辑模式且必填时显示 */}
          {shouldShowUnableUploadCheckbox && (
            <Form.Item formItemKey="unableUpload" span={8} labelWidth={0}>
              <Checkbox toFormItem disabled={disabled}>
                无法上传
              </Checkbox>
            </Form.Item>
          )}

          {/* 预览态下，无法上传 */}
          {!isEditMode && isUnableToUpload && (
            <Form.Item formItemKey="unableUpload" span={8} labelWidth={0}>
              <span>无</span>
            </Form.Item>
          )}

          {/* 可以上传 需要上传附件 */}
          {shouldShowFileUpload && (
            <>
              <Form.Item
                // 写为 fileUrl ，可以让校验更简单
                formItemKey="fileUrl"
                required={required}
                className="upload-wrapper"
                // 处理非必填的时候，labelWidth和span的值
                labelWidth={
                  // 展示【无法上传】勾选框的时候，上传框在下方，为了对齐，保留label宽度
                  shouldShowUnableUploadCheckbox ? labelWidth : 0
                }
                // 不展示【无法上传】勾选框的时候，上传框在上方，需要减小span
                span={shouldShowUnableUploadCheckbox ? 24 : 12}
                message={`${getUploadLabel()}不能为空`}
              >
                {/* 预览态下，如果没有文件，不展示Upload */}
                {isEditMode ? (
                  <Upload
                    toFormItem
                    mode={mode}
                    url={Url.upload}
                    needMoreHeight
                    label={getUploadLabel()}
                    onChange={handleFileChange}
                  />
                ) : (
                  // 预览态下，图片有，则展示img，无，则展示无
                  renderViewFile()
                )}
              </Form.Item>

              {/* 证书编码输入框 - 仅对特定证书类型显示 */}
              {shouldShowCertificateCode && (
                <Form.Item
                  // 证书编码 动态的 formItemKey
                  formItemKey={codeItemKey}
                  message="证书编码不能为空"
                  required={required}
                  label={`${codeItemLabel}：`}
                >
                  <Input toFormItem maxLength={100} disabled={disabled} />
                </Form.Item>
              )}
            </>
          )}

          {/* 无法上传 需要选择原因，可能会上传附件 */}
          {shouldShowReasonSelect && (
            <>
              <Form.Item
                formItemKey="reason"
                required
                rules={[
                  { required: true, message: '请选择替代材料' },
                  {
                    validator(_, reasonObj, callback) {
                      if (!reasonObj) {
                        callback('请选择替代材料');
                        return;
                      }
                      if (!reasonObj.reasonIndex) {
                        callback('请选择替代材料');
                        return;
                      }
                      callback();
                    },
                  },
                ]}
                label="替代材料："
              >
                <Select
                  toFormItem
                  keyField="reasonIndex"
                  labelField="reasonName"
                  onFetchData={handleFetchReason}
                  placeholder="请选择替代材料"
                  disabled={disabled}
                />
              </Form.Item>

              {/* 特定原因需要上传文件 */}
              {shouldShowReasonFileUpload && (
                <Form.Item
                  // 写为 fileUrl ，可以让校验更简单
                  formItemKey="fileUrl"
                  // 能展示的时候，一定是必填的
                  required
                  labelWidth={labelWidth}
                  className="upload-wrapper"
                  span={24}
                  message={`${getUploadLabel()}不能为空`}
                >
                  {/* 预览态下，如果没有文件，不展示Upload */}
                  {isEditMode ? (
                    <Upload
                      toFormItem
                      mode={mode}
                      url={Url.upload}
                      needMoreHeight
                      label={getUploadLabel()}
                      onChange={handleFileChange}
                    />
                  ) : (
                    // 预览态下，图片有，则展示img，无，则展示无
                    renderViewFile()
                  )}
                </Form.Item>
              )}
            </>
          )}
          {/* 预览态下，不展示，并且不能跟Upload绑定，因为可能会出现不展示Upload还展示ReasonTips的情况 */}
          {isEditMode && (
            <Form.Item className="upload-wrapper" labelWidth={labelWidth} span={24}>
              <ReasonTips
                data={data}
                formState={formState}
                certificateType={certificateType}
                reasonList={reasonList}
                openImagePreview={openImagePreview}
              />
            </Form.Item>
          )}
        </Form>

        {/* 确认弹窗 */}
        <ConfirmModal
          state={confirmModalState}
          certificateType={certificateType}
          onOk={handleConfirmOk}
          onCancel={handleConfirmCancel}
        />

        {/* 图片预览Modal */}
        <ImagePreviewModal
          visible={imagePreviewState.visible}
          imageUrl={imagePreviewState.imageUrl}
          title={imagePreviewState.title}
          onClose={closeImagePreview}
        />
      </div>
    );
  },
);

UploadCertificate.displayName = 'UploadCertificate';

export * from './types';
